import { ApiError } from '@/api/client';

/**
 * De un fallo a la frase que lee una persona.
 *
 * Estaba escrito catorce veces, siempre igual —`fallo instanceof ApiError ? fallo.message :
 * '…'`— y cada copia con su propia frase de reserva. No molestaba por repetirse, sino porque no
 * había ningún lugar donde decidir cómo se le habla a alguien cuando algo falla: cambiar el tono
 * eran catorce ediciones y encontrar las catorce.
 *
 * La API manda un mensaje pensado para leerse ("Faltan assets para generar el PDF de BENELLI"),
 * así que cuando viene se usa. La frase de reserva es para lo demás: la red que se cortó, un 500
 * sin cuerpo, algo que ni siquiera era un Error.
 *
 * Es la contraparte de `getApiErrorMessage` de CKINSIGHT, que hace lo mismo con Axios.
 */
export function mensajeDeError(fallo: unknown, generico: string): string {
  if (fallo instanceof ApiError) return fallo.message;

  // Un Error del navegador —"Failed to fetch"— dice poco, pero dice más que la frase genérica
  // cuando lo que pasó es que se cayó la conexión.
  if (fallo instanceof Error && fallo.message) return fallo.message;

  return generico;
}

/**
 * Los estados que significan "ahora no, probá después" y no "esto está roto".
 *
 * 429 es nginx frenando por cantidad de pedidos; 502, 503 y 504 son la API reiniciándose, caída
 * o tardando de más. Ninguno se arregla haciendo nada distinto: se arreglan solos en segundos.
 */
const PASAJEROS = new Set([429, 502, 503, 504]);

/**
 * Qué dice la pantalla de error, según lo que pasó.
 *
 * Antes había dos casos: 404 y "algo se rompió de nuestro lado, escribinos a info@". Para un
 * corte de treinta segundos eso es mentira y además manda a escribir un mail por algo que ya se
 * arregló solo. Son tres situaciones distintas y la persona puede hacer algo distinto en cada
 * una: buscar de nuevo, esperar un momento, o avisarnos.
 */
export function carteDeError(estado: number | null): {
  titulo: string;
  detalle: string;
  /** Si lo que corresponde ofrecer es volver a intentar. */
  pasajero: boolean;
} {
  if (estado === 404) {
    return {
      titulo: 'No encontramos esa página',
      detalle: 'Puede que el enlace esté viejo o que el producto ya no esté en el catálogo.',
      pasajero: false,
    };
  }

  if (estado !== null && PASAJEROS.has(estado)) {
    return {
      titulo: 'El servidor no está respondiendo',
      detalle: 'Es pasajero. Probá de nuevo en un momento.',
      pasajero: true,
    };
  }

  return {
    titulo: 'Algo se rompió de nuestro lado',
    detalle: 'Probá de nuevo en un rato. Si sigue igual, escribinos a info@cking.com.ar.',
    pasajero: false,
  };
}

/**
 * Lo que una pantalla contesta cuando no pudo hablar con la API.
 *
 * Importa el estado, no sólo el texto. Una ficha de producto que devuelve 404 porque la API
 * estaba caída le está diciendo a Google que ese producto ya no existe, y Google le cree: lo
 * saca del índice y volver cuesta semanas. "No contesta" y "no existe" son dos cosas distintas y
 * tienen que salir con números distintos.
 *
 * Un corte —la API reiniciándose, nginx frenando, la red— sale con su propio estado y la pantalla
 * ofrece reintentar. Cualquier otra cosa es un 500 de verdad, y ahí el cartel que corresponde es
 * el de siempre.
 */
export function comoRespuesta(fallo: unknown): Response {
  const estado = fallo instanceof ApiError && PASAJEROS.has(fallo.status) ? fallo.status : null;

  // Sin ApiError no hubo respuesta: se cortó la conexión o la API no está levantada.
  const sinRespuesta = !(fallo instanceof ApiError);

  return new Response(null, {
    status: estado ?? (sinRespuesta ? 503 : 500),
    statusText: estado === null && !sinRespuesta ? 'Error' : 'El servidor no está respondiendo',
  });
}
